import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import React, {
  Suspense,
  lazy,
  useMemo,
  useEffect,
  useState,
  useRef
} from 'react';
import { ToastContainer } from 'react-toastify';
import ProtectedRoute from './components/ProtectedRoute';
import { useEqualWebAccessibility } from './hooks/useEqualWebAccessibility';
import AutoLogout from './hooks/AutoLogout';
import { UnsavedChangesProvider } from './guards/useUnsavedChangesGuard';
import { lazyWithPreload } from './utils/lazyWithPreload';
import WarmCommon from './components/WarmCommon';
import Layout from './components/Layout';
import { PageSkeleton, DashboardSkeleton } from './components/skeletons';
import ChatLauncher from "./components/chat/ChatLauncher";
import ChatPopup from "./components/chat/ChatPopup";
import { socketManager, useSocketSubscription } from "./lib/socketManager";
import { useClient } from './contexts/ClientContext';
import { SocketProvider } from './components/SocketProvider';
import BlockedUserModal from './components/BlockedUserModal';
import BlockedUserSocketModal from './components/BlockedUserSocketModal';
import SessionExpiredModal from './components/SessionExpiredModal';
import ForceRefreshModal from './components/ForceRefreshModal';
import ManagerPermissionsChangedModal from './components/ManagerPermissionsChangedModal';
import GlobalReminderPopup from './components/GlobalReminderPopup';
import StickyNotes from './components/StickyNotes';
import Calculator from './components/Calculator';
import FeatureGuard from './components/FeatureGuard';
import SidebarRouteGuard from './components/SidebarRouteGuard';
import ClientsPageFallback from './components/ClientsPageFallback';
import 'react-toastify/dist/ReactToastify.css';
import PublicLayout from './layouts/PublicLayout';
import { FeaturesProvider } from './contexts/CompanyFeaturesContext';
import { StorageStateProvider } from './contexts/StorageStateContext';
import { BootstrapProvider } from './contexts/BootstrapContext';
import PostLoginLoader from './components/layout/PostLoginLoader';
import { useBootstrap } from './contexts/BootstrapContext';
// LoadingIcon removed - no global loading animations
import { getActiveCompanySlug, getCompanySlugFromLocation, resolveActiveCompanySlug, getRuntimeEnvFromHostname } from './utils/companySlug';
import { isSuperadminPath } from './utils/routeHelpers';
import chatApi, { getConversations } from './api/chatApi';
import { isAdminRole, isMainAccountLevel, canSeeAdminContent, hasFullPermissions } from './config/permissionsConfig';
import { logoutSuperadminToLogin } from './utils/superadminAuth';

// Helper function to patch AI conversations (same as ChatPopup)
function patchAI(c: any): any {
  const BOT_NAME = "Lumira AI";
  const looksAI = Boolean(c.isAI || c.botUserId || /(^|\s)(lumira\s*ai|ai\s*assistant)\b/i.test(c.title || ""));
  return looksAI ? { ...c, isAI: true, title: BOT_NAME } : c;
}

// Lazy loaded pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const LeadsRegister = lazy(() => import('./pages/LeadsRegister'));
const TrialExpired = lazy(() => import('./pages/TrialExpired'));
const Appointments = lazy(() => import('./pages/Appointments'));
const Clients = lazyWithPreload(() => import(/* webpackPrefetch: true */ './pages/Clients'));
const ClientProfile = lazy(() => import('./pages/ClientProfile'));
const SlotAdmin = lazy(() => import('./pages/SlotsAdmin'));
// Dashboard pages - now using routing-based structure
const DashboardLayout = lazy(() => import('./layouts/DashboardLayout'));
const DashboardOverview = lazy(() => import('./pages/dashboard/DashboardOverview'));
const DashboardFunnelPage = lazy(() => import('./pages/dashboard/DashboardFunnelPage'));
const DashboardManagerPage = lazy(() => import('./pages/dashboard/DashboardManagerPage'));
const DashboardBookingsPage = lazy(() => import('./pages/dashboard/DashboardBookingsPage'));
const DashboardTasksPage = lazy(() => import('./pages/dashboard/DashboardTasksPage'));
const DashboardPaymentsPage = lazy(() => import('./pages/dashboard/DashboardPaymentsPage'));
const DashboardLeadsPage = lazy(() => import('./pages/dashboard/DashboardLeadsPage'));
const Home = lazy(() => import('./pages/Home'));
const CalendarBookings = lazy(() => import('./pages/CalendarBookings'));
const MyProfile = lazy(() => import('./pages/MyProfile'));
const ManagerCRM = lazy(() => import('./pages/managerCRM'));
const Managers = lazy(() => import('./pages/managers'));
const ManagerProfile = lazy(() => import('./pages/ManagerProfile'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const SuperAdminGlobalOverview = lazy(() => import('./pages/SuperAdmin/GlobalOverview'));
const SuperAdminInfrastructure = lazy(() => import('./pages/SuperAdmin/InfrastructureStats'));
const SuperAdminAnnouncements = lazy(() => import('./pages/SuperAdmin/Announcements'));
const SuperAdminMail = lazy(() => import('./pages/SuperAdmin/Mail'));
const SuperAdminLogs = lazy(() => import('./pages/SuperAdmin/ServerLogs'));
const SuperAdminOnlineUsers = lazy(() => import('./pages/SuperAdmin/OnlineUsers'));
const SuperAdminCompanies = lazy(() => import('./pages/AdminUsers'));
const SuperAdminTrialManagement = lazy(() => import('./pages/SuperAdmin/TrialManagement'));
const SuperAdminActiveClientsManagement = lazy(() => import('./pages/SuperAdmin/ActiveClientsManagement'));
const SuperAdminCompanyFeatures = lazy(() => import('./pages/SuperAdmin/CompanyFeatures'));
const SuperAdminBilling = lazy(() => import('./pages/SuperAdmin/BillingManagement'));
const SuperAdminAuditLogs = lazy(() => import('./pages/SuperAdmin/AuditLogs'));
const SuperAdminWhiteLabel = lazy(() => import('./pages/SuperAdmin/WhiteLabel'));
const SuperAdminAutomationLimits = lazy(() => import('./pages/SuperAdmin/AutomationLimits'));
const SuperAdminGlobalUsers = lazy(() => import('./pages/SuperAdmin/GlobalUserManagement'));
const SuperAdminCompanyDetails = lazy(() => import('./pages/SuperAdmin/CompanyDetails'));
const SuperAdminCompanyProfile = lazy(() => import('./pages/SuperAdmin/SuperAdminCompanyProfile'));
const SuperAdminCmsPages = lazy(() => import('./pages/SuperAdmin/CmsPagesManagement'));
const SuperAdminIPAccessControl = lazy(() => import('./pages/SuperAdmin/IPAccessControl'));
const SuperAdminDeviceManagement = lazy(() => import('./pages/SuperAdmin/DeviceManagement'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdmin/Dashboard'));
const AdminTasksPage = lazy(() => import('./pages/AdminTasksPage'));
const ManagerTasksPage = lazy(() => import('./pages/ManagerTasksPage'));
const AdminWorkersPage = lazy(() => import('./pages/AdminWorkersPage'));
const ManagerShiftsPage = lazy(() => import('./pages/ManagerShiftsPage'));
const AdminProposalsPage = lazy(() => import('./pages/AdminProposalsPage'));
const AdminProposalForm = lazy(() => import('./pages/AdminProposalForm'));
const PublicProposalPage = lazy(() => import('./pages/PublicProposalPage'));
const About = lazy(() => import('./pages/AboutUs'));
const AgentsPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ './pages/AgentsPage'));
const IssuesPage = lazy(() => import('./pages/issues'));
const AdminIssuesPage = lazy(() => import('./pages/adminIssues'));
const UnifiedCalendarPage = lazy(() => import('./pages/UnifiedCalendarPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const PrivacyPolicyCRM = lazy(() => import('./pages/PrivacyPolicyCRM'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const OAuthGoogleReturn = lazy(() => import('./pages/OAuthGoogleReturn'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const ManagerSettings = lazy(() => import('./pages/ManagerSettings'));
const AgentSettings = lazy(() => import('./pages/AgentSettings'));
const ClientSettings = lazy(() => import('./pages/ClientSettings'));

const Features = lazy(() => import('./pages/SITEMAP/Features'));
const Pricing = lazy(() => import('./pages/SITEMAP/Pricing'));
const AboutSitemap = lazy(() => import('./pages/SITEMAP/About'));
const Contact = lazy(() => import('./pages/SITEMAP/Contact'));
const CrmForSmallBusiness = lazy(() => import('./pages/SITEMAP/CrmForSmallBusiness'));
const CrmAppointments = lazy(() => import('./pages/SITEMAP/CrmAppointments'));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const ManagerLeadsPage = lazy(() => import('./pages/ManagerLeadsPage'));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage'));
const ClientOrdersPage = lazy(() => import('./pages/ClientOrdersPage'));
const InventoryManagementPage = lazy(() => import('./pages/InventoryManagementPage'));
const BugWallPage = lazy(() => import('./pages/BugWallPage'));
const QATestersReport = lazy(() => import('./pages/QATestersReport'));
const QAFinalReport = lazy(() => import('./pages/QAFinalReport'));
const AutomationsPage = lazy(() => import('./pages/AutomationsPage'));
const CreateAutomationPage = lazy(() => import('./pages/CreateAutomationPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const CreateTemplatePage = lazy(() => import('./pages/CreateTemplatePage'));
const AutomationRunsPage = lazy(() => import('./pages/AutomationRunsPage'));
const ContractsList = lazy(() => import('./pages/ContractsList'));
const VendorsList = lazy(() => import('./pages/VendorsList'));
const VendorProfile = lazy(() => import('./pages/VendorProfile'));
const VendorMePage = lazy(() => import('./pages/VendorMePage'));
const VendorCatalogPage = lazy(() => import('./pages/VendorCatalogPage'));
const ContractNew = lazy(() => import('./pages/ContractNew'));
const ContractEditor = lazy(() => import('./pages/ContractEditor'));
const ContractView = lazy(() => import('./pages/ContractView'));
const ContractSign = lazy(() => import('./pages/ContractSign'));
const PublicContractSign = lazy(() => import('./pages/PublicContractSign'));
const PrivateContractSign = lazy(() => import('./pages/PrivateContractSign'));
const MessageLogs = lazy(() => import('./pages/MessageLogs'));
const ContractTemplatesPage = lazy(() => import('./pages/ContractTemplatesPage'));
const ContractTemplateEditor = lazy(() => import('./pages/ContractTemplateEditor'));
const Guide = lazy(() => import('./pages/Guide'));
const GuideAdmin = lazy(() => import('./pages/GuideAdmin'));

// NEW: dual-mode landing wrappers
const LandingPublic = lazy(() => import('./pages/LandingPublic')); // -> <PublicLandingTemplates mode="public" />
const PublicLandingDesigner = lazy(() => import('./pages/PublicLandingDesigner')); // -> <PublicLandingTemplates mode="admin" />

// --- Guards (manager-permission aware) ---
type GuardProps = {
  children: React.ReactNode;
};

type ManagerGuardProps = GuardProps & {
  permAll?: string[];
  permAny?: string[];
};

const ManagerGuard: React.FC<ManagerGuardProps> = ({ permAll, permAny, children }) => {
  const { client: me, loading } = useClient();
  const { company } = useParams();
  const location = useLocation();
  
  // CRITICAL: Skip company-based permission checks for superadmin routes
  if (isSuperadminPath(location.pathname)) {
    // Superadmin routes don't use company-based permissions
    return <>{children}</>;
  }
  
  // NO GLOBAL LOADING: Render children immediately, permissions checked in background
  // UI never blocks - components handle missing data gracefully

  // Admins and superadmins bypass permission checks
  if (isAdminRole(me)) return <>{children}</>;

  // Only enforce for managers; everyone else passes through.
  if (me?.role !== "manager") return <>{children}</>;

  // Managers with full access (גישה מלאה) bypass permission checks like admin
  const isFullAccessManager = hasFullPermissions(me?.permissions || []);
  if (isFullAccessManager) return <>{children}</>;

  // Regular managers must have explicit permissions

  const perms: string[] = Array.isArray(me?.permissions) ? (me!.permissions as string[]) : [];
  const allOk = !permAll || permAll.every((p) => perms.includes(p));
  const anyOk = !permAny || permAny.some((p) => perms.includes(p));

  if (allOk && anyOk) return <>{children}</>;

  // No permission → redirect to home
  console.warn('[ManagerGuard] Permission denied:', {
    role: me?.role,
    requiredPermissions: permAll,
    userPermissions: perms,
    hasAllRequired: allOk,
    hasAnyRequired: anyOk
  });
  
  // CRITICAL: Ensure company is valid before building redirect path
  if (!company || company === 'undefined') {
    console.warn('[ManagerGuard] Cannot redirect to home - invalid company:', company);
    return <>{children}</>;
  }
  
  return <Navigate to={`/${company}/home`} replace />;
};

// Vendor can access /vendors/me (own profile + catalog); admin/manager need view_vendors for any vendor
const VendorProfileGate: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { client } = useClient();
  if (id === 'me' && client?.role === 'vendor') {
    return <Suspense fallback={<PageLoader contentType="form" />}><VendorProfile /></Suspense>;
  }
  return (
    <RequireRole roles={["admin", "manager"]}>
      <ManagerGuard permAll={["view_vendors"]}>
        <Suspense fallback={<PageLoader contentType="form" />}><VendorProfile /></Suspense>
      </ManagerGuard>
    </RequireRole>
  );
};

// Strict role requirement (for pure manager-only pages like /manager-crm)
const RequireRole: React.FC<GuardProps & { roles: string[] }> = ({ roles, children }) => {
  const { client: me, loading } = useClient();
  const { company } = useParams();
  const location = useLocation();
  
  // CRITICAL: For superadmin routes, only check role (not company)
  if (isSuperadminPath(location.pathname)) {
    // For superadmin routes, only check if user has superadmin role
    if (me && me.role === 'superadmin' && roles.includes('superadmin')) {
      return <>{children}</>;
    }
    // If superadmin route but not superadmin role, redirect to superadmin login
    // Use logoutSuperadminToLogin to ensure it goes through forceLogout fuse
    setTimeout(() => {
      logoutSuperadminToLogin('RequireRole::superadmin_route_no_role', {
        reason: 'superadmin_auth_failed',
      });
    }, 0);
    return null; // Render nothing while redirecting
  }
  
  // NO GLOBAL LOADING: Render children immediately, role checked in background
  // UI never blocks - components handle missing data gracefully
  
  // Check if manager has full access (גישה מלאה) - they can access admin routes
  const isFullAccessManager = me?.role === 'manager' && hasFullPermissions(me?.permissions || []);
  
  // Allow access if user has one of the required roles
  const hasAccess = me && roles.includes(me.role as string);
  
  // Managers with full access can access admin routes
  if (!hasAccess && isFullAccessManager && roles.includes('admin')) {
    return <>{children}</>;
  }
  
  // Special case: main account (vp@wjsinnovations.com) can access admin routes
  if (!hasAccess && isMainAccountLevel(me) && roles.includes('admin')) {
    return <>{children}</>;
  }
  
  if (hasAccess) return <>{children}</>;

  // Not the right role → redirect to home
  // CRITICAL: Ensure company is valid before building redirect path
  if (!company || company === 'undefined') {
    console.warn('[RequireRole] Cannot redirect to home - invalid company:', company);
    return <>{children}</>;
  }
  
  return <Navigate to={`/${company}/home`} replace />;
};
//-------------------------------------------//

// Public marketing main landing (if you have one)
const MainLanding = lazy(() => import('./pages/MainLanding'));

// Fallback loader component - positioned to replace content, not stack with it
const Loader = () => (
  <div className="flex items-center justify-center py-20 min-h-[200px]">
    {/* Minimal loading placeholder - no animation */}
  </div>
);

// Enhanced fallback for pages - uses PageSkeleton for better UX
const PageLoader = ({ contentType = 'default' as 'table' | 'cards' | 'list' | 'form' | 'default' }) => (
  <PageSkeleton contentType={contentType} showHeader showToolbar />
);

const LeadsIndex: React.FC = () => {
  const { client: me } = useClient();
  const { company } = useParams();
  // Managers with full access (גישה מלאה) can see all leads like admin
  // Regular managers should see their own leads only
  const isFullAccessManager = me?.role === 'manager' && hasFullPermissions(me?.permissions || []);
  if (me?.role === 'manager' && !isFullAccessManager && !canSeeAdminContent(me)) {
    return <Navigate to={`/${company}/leads/my`} replace />;
  }
  return <LeadsPageWrapper />;
};

// Helper: should a message increment unread? (same logic as ChatPopup)
function shouldCountUnread(message: any, convId: string, isActive: boolean, meId: string, lastReadAt: Record<string, string>): boolean {
  if (!message) return false;
  const isMine = String(message.createdBy) === meId;
  if (isMine) return false;
  if (isActive) return false;

  // if server says you've read it, don't count
  const alreadyRead = Array.isArray(message.readBy) && message.readBy.some((r: any) => String(r.userId) === meId);
  if (alreadyRead) return false;

  // if it's older/equal to our last-read, don't count
  const lastReadIso = lastReadAt[convId];
  if (lastReadIso && message.createdAt) {
    const msgT = new Date(message.createdAt).getTime();
    const lastT = new Date(lastReadIso).getTime();
    if (msgT <= lastT) return false;
  }
  return true;
}

const ChatMount: React.FC = () => {
  const { company } = useParams<{ company: string }>();
  const [open, setOpen] = useState(false);
  const [openLumiraAI, setOpenLumiraAI] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const lastReadAtRef = useRef<Record<string, string>>({});

  // read current user ("client") saved by your login flow
  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("client") || "{}"); } catch { return {}; }
  }, []);
  const userId = me?._id as string | undefined;
  
  // Don't show chat for clients - only staff (admin, manager, agent)
  const allowedRoles = ['admin', 'manager', 'agent', 'superadmin'];
  const userRole = me?.role as string | undefined;
  const canUseChat = !userRole || allowedRoles.includes(userRole);

  // Track unread from ChatPopup when popup is open (for showing 0)
  const [unreadFromPopup, setUnreadFromPopup] = useState<number | null>(null);

  // Calculate unread count the same way as ChatPopup does
  // Use unreadFromPopup when popup is open, otherwise calculate from conversations
  const unread = useMemo(() => {
    if (open && unreadFromPopup !== null) {
      return unreadFromPopup; // Show 0 when popup is open
    }
    return conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  }, [conversations, open, unreadFromPopup]);


  // Load initial conversations (same as ChatPopup)
  useEffect(() => {
    if (!company || !userId || !canUseChat) return;
    if (company === 'superadmin' || me?.role === 'superadmin') {
      setConversations([]);
      return;
    }

    let alive = true;
    (async () => {
      try {
        const list = await getConversations(company);
        if (!alive) return;
        const patched = (Array.isArray(list) ? list.map(patchAI) : []).map((c: any) => ({
          ...c,
          lastForMe: c.lastForMe || c.lastMessage || null,
        }));
        setConversations(patched);
      } catch (e) {
        console.error('Failed to load conversations', e);
      }
    })();
    return () => { alive = false; };
  }, [company, userId, me?.role, canUseChat]);

  // Listen to socket events and update conversations the same way as ChatPopup
  // Use socketManager.subscribe instead of direct socket.on
  useEffect(() => {
    if (!company || !userId || !canUseChat) return;
    if (company === 'superadmin' || me?.role === 'superadmin') return;

    // Handle new message events - VISUAL ONLY (no unreadCount changes)
    // unreadCount is managed ONLY by backend via conversation:updated events
    const onMsg = ({ conversationId, message }: { conversationId: string; message: any }) => {
      setConversations(prev => {
        const idx = prev.findIndex(c => String(c._id) === String(conversationId));
        
        const updatedLast = {
          _id: message._id,
          textPreview: (message.text || "").slice(0, 120),
          sentAt: message.createdAt,
          sentBy: message.createdBy,
        };

        if (idx === -1) {
          // New conversation - add it WITHOUT unreadCount (will be set by backend via conversation:updated)
          // CRITICAL: Do NOT set unreadCount: 0 here - it causes flicker. Backend will send correct value.
          const stub: any = patchAI({
            _id: conversationId,
            type: "dm",
            participants: [{ userId: message.createdBy }],
            lastMessage: updatedLast,
            // unreadCount is NOT set here - backend will send it via conversation:updated
          });
          stub.lastForMe = updatedLast;
          return [stub, ...prev];
        }

        // Existing conversation - update lastMessage only, preserve unreadCount
        const c = prev[idx];
        const next = [...prev];
        const patched: any = patchAI({
          ...c,
          lastMessage: updatedLast,
          // CRITICAL: Do NOT modify unreadCount here - backend will send conversation:updated with correct value
        });
        patched.lastForMe = updatedLast;
        next[idx] = patched;

        // Move updated conv to top for visibility
        const [moved] = next.splice(idx, 1);
        return [moved, ...next];
      });
    };

    const onRead = ({ conversationId, by, at }: { conversationId: string; by?: string; at?: string }) => {
      if (!by) return;

      if (String(by) === String(userId)) {
        if (at) lastReadAtRef.current[conversationId] = at;
        else lastReadAtRef.current[conversationId] = new Date().toISOString();

        setConversations(prev =>
          prev.map(c => (c._id === conversationId ? { ...c, unreadCount: 0 } : c))
        );
      }
    };

    // Handle conversation:updated / conversation:created - SOURCE OF TRUTH for unreadCount
    // This is the ONLY place where unreadCount is updated in the frontend
    const onConv = (c: any) => {
      const patched = patchAI(c);
      const isParticipant = patched.participants?.some((p: any) => String(p.userId) === String(userId));
      
      if (isParticipant) {
        setConversations(prev => {
          const existing = prev.find(x => String(x._id) === String(patched._id));
          
          if (!existing) {
            // New conversation - add it with unreadCount from backend
            const withPreview: any = { 
              ...patched,
              unreadCount: patched.unreadCount ?? 0, // Use backend value directly
              lastForMe: patched.lastForMe || patched.lastMessage || null 
            };
            return [withPreview, ...prev.filter(x => String(x._id) !== String(patched._id))];
          }
          
          const copy = prev.filter(x => String(x._id) !== String(patched._id));
          
          // CRITICAL: Use backend unreadCount as source of truth
          // Since we removed all optimistic updates, we can trust backend completely
          const withPreview: any = { 
            ...patched,
            unreadCount: patched.unreadCount ?? existing.unreadCount ?? 0, // Backend is source of truth
            lastForMe: patched.lastForMe || patched.lastMessage || existing?.lastForMe || null 
          };
          return [withPreview, ...copy];
        });
      }
    };

    // Handle reaction events - update "הגיב באימוגי" preview (VISUAL ONLY - no unreadCount changes)
    const onReaction = ({ messageId, emoji, userId: reactionUserId, conversationId }: { messageId: string; emoji: string; userId?: string; conversationId?: string }) => {
      if (!reactionUserId || !conversationId) return;
      
      setConversations(prev => {
        const idx = prev.findIndex(c => String(c._id) === String(conversationId));
        
        if (idx === -1) return prev;
        
        const c = prev[idx];
        const prevLast = c.lastMessage || {};
        
        const updatedLast = {
          ...prevLast,
          _id: messageId,
          textPreview: `הגיב באימוגי ${emoji}`,
          sentAt: prevLast.sentAt || new Date().toISOString(),
          sentBy: reactionUserId,
          tombstone: false,
        };
        
        const next = [...prev];
        const patched: any = patchAI({
          ...c,
          lastMessage: updatedLast,
        });
        (patched as any).lastForMe = updatedLast;
        next[idx] = patched;
        
        const [moved] = next.splice(idx, 1);
        return [moved, ...next];
      });
    };

    // Refresh conversations on badge:poke (server tells us to refresh)
    const onBadgePoke = async () => {
      try {
        const list = await getConversations(company);
        const patched = (Array.isArray(list) ? list.map(patchAI) : []).map((c: any) => ({
          ...c,
          lastForMe: c.lastForMe || c.lastMessage || null,
        }));
        setConversations(patched);
      } catch (e) {
        console.error('Failed to refresh conversations on badge:poke', e);
      }
    };

    // Subscribe to socket events using socketManager
    const unsubMsg = socketManager.subscribe("message:new", onMsg);
    const unsubRead = socketManager.subscribe("messages:read", onRead);
    const unsubConvCreated = socketManager.subscribe("conversation:created", onConv);
    const unsubConvUpdated = socketManager.subscribe("conversation:updated", onConv);
    const unsubBadgePoke = socketManager.subscribe("badge:poke", onBadgePoke);
    const unsubReaction = socketManager.subscribe("message:reaction", onReaction);

    return () => {
      unsubMsg();
      unsubRead();
      unsubConvCreated();
      unsubConvUpdated();
      unsubBadgePoke();
      unsubReaction();
    };
  }, [company, userId, canUseChat, me?.role]);


  // Listen for sidebar chat/lumira-ai clicks
  useEffect(() => {
    const handleChatOpen = () => {
      if (!canUseChat) return;
      setOpen(true);
      setOpenLumiraAI(false);
    };
    const handleLumiraAIOpen = () => {
      if (!canUseChat) return;
      setOpen(true);
      setOpenLumiraAI(true);
    };

    window.addEventListener('open-chat', handleChatOpen);
    window.addEventListener('open-lumira-ai', handleLumiraAIOpen);

    return () => {
      window.removeEventListener('open-chat', handleChatOpen);
      window.removeEventListener('open-lumira-ai', handleLumiraAIOpen);
    };
  }, [canUseChat]);

  // Don't render chat for clients - only staff (admin, manager, agent)
  // CRITICAL: Render ChatLauncher even if socket is null to prevent remount
  // ChatLauncher is visual-only and doesn't need socket
  // Only ChatPopup needs socket, so we conditionally render it
  if (!canUseChat || !company || !userId) return null;

  return (
    <>
      {/* ChatLauncher is always rendered (with key to prevent remount) */}
      <ChatLauncher key="chat-launcher-fixed" unread={unread} onOpen={() => { setOpen(true); setOpenLumiraAI(false); }} />
      {/* ChatPopup only renders when popup is open (uses socketManager internally) */}
      {open && (
        <ChatPopup
          isOpen={open}
          onClose={() => { setOpen(false); setOpenLumiraAI(false); }}
          company={company}
          me={{ _id: userId, fullName: me?.fullName || "Me" }}
          onUnreadChange={setUnreadFromPopup}
          openLumiraAI={openLumiraAI}
        />
      )}
    </>
  );
};

// Utility
const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch {
    return true;
  }
};

// Component to handle redirect when accessing /:company without path
const CompanyRedirect = () => {
  const { company } = useParams();
  const location = useLocation();
  const [isResolving, setIsResolving] = useState(true);
  const rawToken = localStorage.getItem('token');
  const token = useMemo(() => (rawToken && !isTokenExpired(rawToken) ? rawToken : null), [rawToken]);
  
  useEffect(() => {
    // Small delay to prevent flash during redirect resolution
    const timer = setTimeout(() => setIsResolving(false), 50);
    return () => clearTimeout(timer);
  }, []);
  
  if (isResolving) {
    return <Loader />;
  }
  
  // CRITICAL: If this is a superadmin route, do NOT process as company route
  // This component should only handle /:company routes, not /superadmin routes
  if (location.pathname.startsWith('/superadmin')) {
    // This shouldn't happen, but if it does, just render nothing (let superadmin routes handle it)
    return null;
  }

  // Public leads register: treat as public page, never redirect to /leadsregister/home
  if (location.pathname === '/leadsregister' || location.pathname.startsWith('/leadsregister/')) {
    return <Navigate to="/leadsregister" replace />;
  }

  // Check if path is /undefined or company is undefined
  if (location.pathname === '/undefined' || company === 'undefined' || !company) {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        if (payload?.role === 'superadmin') {
          return <Navigate to="/superadmin/home" replace />;
        }
      } catch {}
    }
    // If no token or not superadmin, try to resolve company
    const env = getRuntimeEnvFromHostname();
    const resolvedCompany = resolveActiveCompanySlug({ env });
    if (resolvedCompany && resolvedCompany !== 'undefined') {
      return <Navigate to={`/${resolvedCompany}/${token ? 'home' : 'login'}`} replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  // Resolve company from URL first (URL is always source of truth)
  const slugFromUrl = getCompanySlugFromLocation();
  const env = getRuntimeEnvFromHostname();
  
  // If URL has a company slug, use it
  if (slugFromUrl) {
    return <Navigate to={`/${slugFromUrl}/${token ? 'home' : 'login'}`} replace />;
  }
  
  // If no company in URL, try to resolve from other sources
  const resolvedCompany = resolveActiveCompanySlug({ env });
  
  // If no company found and no token, redirect to root
  if (!resolvedCompany && !token) {
    return <Navigate to="/" replace />;
  }
  
  // If resolved company exists, use it
  if (resolvedCompany && resolvedCompany !== 'undefined') {
    return <Navigate to={`/${resolvedCompany}/${token ? 'home' : 'login'}`} replace />;
  }
  
  // Last resort: check if superadmin
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload?.role === 'superadmin') {
        return <Navigate to="/superadmin/home" replace />;
      }
    } catch {}
  }
  
  return <Navigate to="/" replace />;
};

// Component to handle redirect for unknown paths
const UnknownPathRedirect = () => {
  const location = useLocation();
  const rawToken = localStorage.getItem('token');
  const token = useMemo(() => (rawToken && !isTokenExpired(rawToken) ? rawToken : null), [rawToken]);
  
  // CRITICAL: Handle superadmin routes first - do NOT try to resolve company slug
  if (location.pathname.startsWith('/superadmin')) {
    // Superadmin route - should already be handled by routes above, but if we get here, redirect to home
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        if (payload?.role === 'superadmin') {
          return <Navigate to="/superadmin/home" replace />;
        }
      } catch {}
    }
    // Not authenticated as superadmin, redirect to login
    // Use logoutSuperadminToLogin to ensure it goes through forceLogout fuse
    setTimeout(() => {
      logoutSuperadminToLogin('UnknownPathRedirect::superadmin_not_authenticated', {
        reason: 'superadmin_auth_failed',
      });
    }, 0);
    return null; // Render nothing while redirecting
  }
  
  // Handle /undefined routes - redirect to superadmin if authenticated as superadmin
  if (location.pathname.startsWith('/undefined')) {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        if (payload?.role === 'superadmin') {
          return <Navigate to="/superadmin/home" replace />;
        }
      } catch {}
    }
    // Not superadmin, try to resolve company
    const env = getRuntimeEnvFromHostname();
    const resolvedCompany = resolveActiveCompanySlug({ env });
    if (resolvedCompany && resolvedCompany !== 'undefined') {
      return <Navigate to={`/${resolvedCompany}/${token ? 'home' : 'login'}`} replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  // Try to get company from URL path first (URL is always source of truth)
  const slugFromUrl = getCompanySlugFromLocation();
  const env = getRuntimeEnvFromHostname();
  
  // If URL has a company slug, use it
  if (slugFromUrl) {
    // CRITICAL: Validate slug before building redirect path
    if (slugFromUrl === 'undefined' || !slugFromUrl.trim()) {
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1] || ''));
          if (payload?.role === 'superadmin') {
            return <Navigate to="/superadmin/home" replace />;
          }
        } catch {}
      }
      return <Navigate to="/" replace />;
    }
    
    if (token) {
      return <Navigate to={`/${slugFromUrl}/home`} replace />;
    }
    return <Navigate to={`/${slugFromUrl}/login`} replace />;
  }
  
  // If no company in URL, try to resolve from other sources
  const resolvedCompany = resolveActiveCompanySlug({ env });
  
  // Don't redirect to undefined routes
  if (resolvedCompany === 'undefined' || !resolvedCompany) {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        if (payload?.role === 'superadmin') {
          return <Navigate to="/superadmin/home" replace />;
        }
      } catch {}
    }
    return <Navigate to="/" replace />;
  }
  
  // CRITICAL: Validate resolved company before building redirect path
  if (resolvedCompany === 'undefined' || !resolvedCompany.trim()) {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        if (payload?.role === 'superadmin') {
          return <Navigate to="/superadmin/home" replace />;
        }
      } catch {}
    }
    return <Navigate to="/" replace />;
  }
  
  if (token && resolvedCompany) {
    return <Navigate to={`/${resolvedCompany}/home`} replace />;
  }
  
  return <Navigate to="/" replace />;
};

const CompanyRoutes = React.memo(() => {
  const rawToken = localStorage.getItem('token');

  useEffect(() => {
    if (rawToken && isTokenExpired(rawToken)) {
      localStorage.removeItem('token');
      localStorage.removeItem('client');
    }
  }, [rawToken]);

  const UnifiedCalendarWrapper = () => {
    const { company } = useParams<{ company: string }>();
    const client = JSON.parse(localStorage.getItem('client') || '{}');
    const adminId = client?.role === 'admin' ? client._id : client?.adminId;

    if (!company || !adminId) return <Loader />;
    return <UnifiedCalendarPage adminId={adminId} company={company} />;
  };

  return (
    <Routes>
      {/* NEW: Public Main Landing (marketing) */}
      <Route path="/" element={
        <Suspense fallback={<Loader />}>
          <MainLanding />
        </Suspense>
      } />

      {/* Public SEO pages (no login) */}
      <Route path="/features" element={<Suspense fallback={<Loader />}><Features /></Suspense>} />
      <Route path="/pricing" element={<Suspense fallback={<Loader />}><Pricing /></Suspense>} />
      <Route path="/about" element={<Suspense fallback={<Loader />}><AboutSitemap /></Suspense>} />
      <Route path="/contact" element={<Suspense fallback={<Loader />}><Contact /></Suspense>} />
      <Route path="/crm-for-small-business" element={<Suspense fallback={<Loader />}><CrmForSmallBusiness /></Suspense>} />
      <Route path="/crm-appointments" element={<Suspense fallback={<Loader />}><CrmAppointments /></Suspense>} />
      <Route path="/leadsregister/*" element={<Suspense fallback={<Loader />}><LeadsRegister /></Suspense>} />

      {/* Public auth routes */}
      <Route path={`/:company/login`} element={<Suspense fallback={<Loader />}><Login /></Suspense>} />
      <Route path={`/:company/register`} element={<Suspense fallback={<Loader />}><Register /></Suspense>} />
      <Route path="/register" element={<Suspense fallback={<Loader />}><Register /></Suspense>} />
      <Route path={`/:company/trial-expired`} element={<Suspense fallback={<Loader />}><TrialExpired /></Suspense>} />
      <Route path="/superadmin/login" element={<Suspense fallback={<Loader />}><Login /></Suspense>} />

      {/* SuperAdmin routes without company parameter */}
      <Route element={<ProtectedRoute />}>
        <Route
          element={
            <AutoLogout>
              <UnsavedChangesProvider>
                <WarmCommon />
                <Layout />
                <FeatureGuard feature="chat" fallback={null}>
                  <ChatMount />
                </FeatureGuard>
              </UnsavedChangesProvider>
            </AutoLogout>
          }
        >
          <Route path="/superadmin/home" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<PageLoader contentType="cards" />}><SuperAdminDashboard /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/overview" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<PageLoader contentType="cards" />}><SuperAdminGlobalOverview /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/infrastructure" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminInfrastructure /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/announcements" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminAnnouncements /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/mail" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminMail /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/logs" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminLogs /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/online-users" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminOnlineUsers /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/companies" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<PageLoader contentType="table" />}><SuperAdminCompanies /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/trial-management" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminTrialManagement /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/active-clients" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminActiveClientsManagement /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/company-features" element={
            <FeatureGuard feature="company-features">
              <Suspense fallback={<Loader />}><SuperAdminCompanyFeatures /></Suspense>
            </FeatureGuard>
          } />
          <Route path="/superadmin/cms-pages" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminCmsPages /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/billing" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminBilling /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/audit-logs" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminAuditLogs /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/white-label" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminWhiteLabel /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/device-management" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminDeviceManagement /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/automation-limits" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminAutomationLimits /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/global-users" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminGlobalUsers /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/issues" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><AdminIssuesPage /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/company/:companyId" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminCompanyProfile /></Suspense>
          </RequireRole>} />
          {/* Legacy route - kept for reference, can be removed after migration */}
          {/* <Route path="/superadmin/company/:companyId/legacy" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminCompanyDetails /></Suspense>
          </RequireRole>} /> */}
          <Route path="/superadmin/company/:companyId/ip-policy" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><SuperAdminIPAccessControl /></Suspense>
          </RequireRole>} />
          <Route path="/superadmin/guide-admin" element={<RequireRole roles={["superadmin"]}>
            <Suspense fallback={<Loader />}><GuideAdmin /></Suspense>
          </RequireRole>} />
        </Route>
      </Route>

      {/* ✅ PUBLIC external landing (no auth, minimal layout) */}
      <Route element={<PublicLayout />}>
        <Route
          path="/lp/:company/landing"
          element={
            <Suspense fallback={<Loader />}>
              <LandingPublic />
            </Suspense>
          }
        />
        <Route
          path="/:company/contracts/sign-public/:token"
          element={
            <Suspense fallback={<Loader />}>
              <PublicContractSign />
            </Suspense>
          }
        />
        <Route
          path="/:company/contracts/sign-private/:token"
          element={
            <Suspense fallback={<Loader />}>
              <PrivateContractSign />
            </Suspense>
          }
        />
      </Route>

      {/* 🔐 PROTECTED AREA (CRM app shell) */}
      <Route element={<ProtectedRoute />}>
        <Route
          element={
            <AutoLogout>
              <UnsavedChangesProvider>
                <WarmCommon />
                <Layout />
                <FeatureGuard feature="chat" fallback={null}>
                  <ChatMount />
                </FeatureGuard>
              </UnsavedChangesProvider>
            </AutoLogout>
          }
        >
          {/* Admin designer version of the landing (inside CRM shell) */}
          <Route
            path="/:company/landing"
            element={
              <FeatureGuard feature="landing-page">
                <Suspense fallback={<Loader />}>
                  <PublicLandingDesigner />
                </Suspense>
              </FeatureGuard>
            }
          />

          <Route path={`/:company/home`} element={<RequireRole roles={["client", "admin", "manager", "agent", "vendor"]}>
            <Suspense fallback={<PageLoader contentType="cards" />}><Home /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/appointments`} element={<SidebarRouteGuard>
            <RequireRole roles={["client", "admin", "manager", "agent"]}>
              <Suspense fallback={<Loader />}><Appointments /></Suspense>
            </RequireRole>
          </SidebarRouteGuard>} />
          <Route path={`/:company/calendar`} element={<RequireRole roles={["client", "admin", "manager", "agent"]}>
            <Suspense fallback={<Loader />}><CalendarBookings /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/messaging/logs`} element={<RequireRole roles={["admin", "manager", "agent"]}>
            <Suspense fallback={<Loader />}>
              <MessageLogs />
            </Suspense>
          </RequireRole>} />
          <Route path={`/:company/clients`} element={<RequireRole roles={["admin", "manager", "agent"]}>
            <FeatureGuard feature="clients">
              <ManagerGuard permAll={["manage_clients"]}>
                {/* Clients page renders structure immediately - fallback shows minimal structure to avoid white flash */}
                <Suspense fallback={<ClientsPageFallback />}>
                  <Clients />
                </Suspense>
              </ManagerGuard>
            </FeatureGuard>
          </RequireRole>} />
          <Route path={`/:company/client/:id`} element={<RequireRole roles={["admin", "manager", "agent"]}>
            <FeatureGuard feature="clients">
              <ManagerGuard permAny={["view_clients", "view_client_details"]}>
                <Suspense fallback={<Loader />}><ClientProfile /></Suspense>
              </ManagerGuard>
            </FeatureGuard>
          </RequireRole>} />
          <Route path={`/:company/admin/slots`} element={<RequireRole roles={["admin", "manager", "agent"]}>
            <FeatureGuard feature="slots">
              <ManagerGuard permAll={["view_slots"]}>
                <Suspense fallback={<Loader />}><SlotAdmin /></Suspense>
              </ManagerGuard>
            </FeatureGuard>
          </RequireRole>} />
          {/* Dashboard with nested routes */}
          <Route path={`/:company/dashboard`} element={<RequireRole roles={["admin", "manager", "agent"]}>
            <FeatureGuard feature="dashboard">
              <ManagerGuard permAll={["overview"]}>
                <Suspense fallback={<Loader />}>
                  <DashboardLayout />
                </Suspense>
              </ManagerGuard>
            </FeatureGuard>
          </RequireRole>}>
            <Route index element={<Suspense fallback={<DashboardSkeleton />}><DashboardOverview /></Suspense>} />
            <Route path="funnel" element={<Suspense fallback={<Loader />}><DashboardFunnelPage /></Suspense>} />
            <Route path="manager" element={<Suspense fallback={<Loader />}><DashboardManagerPage /></Suspense>} />
            <Route path="bookings" element={<Suspense fallback={<Loader />}><DashboardBookingsPage /></Suspense>} />
            <Route path="tasks" element={<Suspense fallback={<Loader />}><DashboardTasksPage /></Suspense>} />
            <Route path="payments" element={<Suspense fallback={<Loader />}><DashboardPaymentsPage /></Suspense>} />
            <Route path="leads" element={<Suspense fallback={<Loader />}><DashboardLeadsPage /></Suspense>} />
          </Route>
          <Route path={`/:company/my-profile`} element={<RequireRole roles={["client", "admin", "manager", "agent"]}>
            <Suspense fallback={<Loader />}><MyProfile /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/manager-crm`} element={<RequireRole roles={["manager"]}>
            <Suspense fallback={<Loader />}><ManagerCRM /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/managers`} element={<FeatureGuard feature="managers">
            <RequireRole roles={["admin", "manager"]}>
              <ManagerGuard permAll={["view_manager"]}>
                <Suspense fallback={<PageLoader contentType="table" />}><Managers /></Suspense>
              </ManagerGuard>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/admin/manager/:id`} element={<FeatureGuard feature="managers">
            <RequireRole roles={["admin", "manager"]}>
              <ManagerGuard permAll={["edit_manager"]}>
                <Suspense fallback={<Loader />}><ManagerProfile /></Suspense>
              </ManagerGuard>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/admin-users`} element={<RequireRole roles={["admin"]}>
            <Suspense fallback={<Loader />}><AdminUsers /></Suspense>
          </RequireRole>} />
          {/* REMOVED: Duplicate superadmin routes under /:company/superadmin/* 
              These were causing /superadmin/home to match /:company/superadmin/* with company=undefined
              Superadmin routes are correctly defined at /superadmin/* above (lines 541-600)
          */}
          <Route path={`/:company/admin/tasks`} element={<FeatureGuard feature="tasks">
            <RequireRole roles={["admin", "manager"]}>
              <ManagerGuard permAll={["view_tasks"]}>
                <Suspense fallback={<PageLoader contentType="table" />}><AdminTasksPage /></Suspense>
              </ManagerGuard>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/admin/workers`} element={<RequireRole roles={["admin"]}>
            <Navigate to="managers" replace />
          </RequireRole>} />
          <Route path={`/:company/admin/workers/:tab`} element={<RequireRole roles={["admin"]}>
            <Suspense fallback={<Loader />}><AdminWorkersPage /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/manager/shifts`} element={<RequireRole roles={["manager"]}>
            <Suspense fallback={<Loader />}><ManagerShiftsPage /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/admin/orders`} element={<RequireRole roles={["admin", "manager", "agent"]}>
            <FeatureGuard feature="orders">
              <ManagerGuard permAll={["manage_orders"]}>
                <Suspense fallback={<PageLoader contentType="table" />}><AdminOrdersPage /></Suspense>
              </ManagerGuard>
            </FeatureGuard>
          </RequireRole>} />
          <Route path={`/:company/admin/proposals`} element={<RequireRole roles={["admin", "manager", "vendor"]}>
            <ManagerGuard permAny={["view_proposals"]}>
              <Suspense fallback={<PageLoader contentType="table" />}><AdminProposalsPage /></Suspense>
            </ManagerGuard>
          </RequireRole>} />
          <Route path={`/:company/admin/proposals/new`} element={<RequireRole roles={["admin", "manager", "vendor"]}>
            <ManagerGuard permAny={["create_proposals"]}>
              <Suspense fallback={<PageLoader contentType="form" />}><AdminProposalForm /></Suspense>
            </ManagerGuard>
          </RequireRole>} />
          <Route path={`/:company/admin/proposals/:id`} element={<RequireRole roles={["admin", "manager", "vendor"]}>
            <ManagerGuard permAny={["edit_proposals"]}>
              <Suspense fallback={<PageLoader contentType="form" />}><AdminProposalForm /></Suspense>
            </ManagerGuard>
          </RequireRole>} />
          {/* Public proposal page (no auth required) */}
          <Route path={`/:company/p/:token`} element={
            <Suspense fallback={<Loader />}><PublicProposalPage /></Suspense>
          } />
          <Route path={`/:company/contracts`} element={<RequireRole roles={["admin", "manager", "vendor"]}>
            <Suspense fallback={<PageLoader contentType="table" />}><ContractsList /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/vendor/me`} element={<RequireRole roles={["vendor"]}>
            <Suspense fallback={<PageLoader contentType="form" />}><VendorMePage /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/vendor/catalog`} element={<RequireRole roles={["vendor"]}>
            <Suspense fallback={<PageLoader contentType="table" />}><VendorCatalogPage /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/vendors`} element={<RequireRole roles={["admin", "manager"]}>
            <ManagerGuard permAll={["view_vendors"]}>
              <Suspense fallback={<PageLoader contentType="table" />}><VendorsList /></Suspense>
            </ManagerGuard>
          </RequireRole>} />
          <Route path={`/:company/vendors/:id`} element={<VendorProfileGate />} />
          <Route path={`/:company/contracts/new`} element={<RequireRole roles={["admin", "manager", "vendor"]}>
            <Suspense fallback={<Loader />}><ContractNew /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/contracts/view/:id`} element={<RequireRole roles={["admin", "manager", "agent", "vendor"]}>
            <Suspense fallback={<Loader />}><ContractView /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/contracts/editor/:id`} element={<RequireRole roles={["admin", "manager", "vendor"]}>
            <Suspense fallback={<Loader />}><ContractEditor /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/contracts/sign/:id`} element={<RequireRole roles={["admin", "manager", "vendor"]}>
            <Suspense fallback={<Loader />}><ContractSign /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/contracts/templates`} element={<RequireRole roles={["admin", "manager"]}>
            <Suspense fallback={<Loader />}><ContractTemplatesPage /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/contracts/templates/new`} element={<RequireRole roles={["admin", "manager"]}>
            <Suspense fallback={<Loader />}><ContractTemplateEditor /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/contracts/templates/edit/:id`} element={<RequireRole roles={["admin", "manager"]}>
            <Suspense fallback={<Loader />}><ContractTemplateEditor /></Suspense>
          </RequireRole>} />
    <Route path={`/:company/admin/inventory`} element={<FeatureGuard feature="inventory">
      <RequireRole roles={["admin"]}>
        <Suspense fallback={<Loader />}><InventoryManagementPage /></Suspense>
      </RequireRole>
    </FeatureGuard>} />
    <Route path={`/:company/bug-wall`} element={<RequireRole roles={["admin"]}>
      <Suspense fallback={<Loader />}><BugWallPage /></Suspense>
    </RequireRole>} />
    <Route path={`/:company/qa-report`} element={<RequireRole roles={["admin"]}>
      <Suspense fallback={<Loader />}><QATestersReport /></Suspense>
    </RequireRole>} />
    <Route path={`/:company/qa-final-report`} element={<RequireRole roles={["admin"]}>
      <Suspense fallback={<Loader />}><QAFinalReport /></Suspense>
    </RequireRole>} />
    <Route path={`/:company/admin/automations`} element={<RequireRole roles={["admin"]}>
      <FeatureGuard feature="automations">
        <Suspense fallback={<Loader />}><AutomationsPage /></Suspense>
      </FeatureGuard>
    </RequireRole>} />
    <Route path={`/:company/admin/automations/new`} element={<RequireRole roles={["admin"]}>
      <FeatureGuard feature="automations">
        <Suspense fallback={<Loader />}><CreateAutomationPage /></Suspense>
      </FeatureGuard>
    </RequireRole>} />
    <Route path={`/:company/admin/automations/:id`} element={<RequireRole roles={["admin"]}>
      <FeatureGuard feature="automations">
        <Suspense fallback={<Loader />}><CreateAutomationPage /></Suspense>
      </FeatureGuard>
    </RequireRole>} />
    <Route path={`/:company/admin/templates`} element={<RequireRole roles={["admin"]}>
      <Suspense fallback={<Loader />}><TemplatesPage /></Suspense>
    </RequireRole>} />
    <Route path={`/:company/admin/templates/new`} element={<RequireRole roles={["admin"]}>
      <Suspense fallback={<Loader />}><CreateTemplatePage /></Suspense>
    </RequireRole>} />
    <Route path={`/:company/admin/templates/:id`} element={<RequireRole roles={["admin"]}>
      <Suspense fallback={<Loader />}><CreateTemplatePage /></Suspense>
    </RequireRole>} />
    <Route path={`/:company/admin/automations/:automationId/runs`} element={<RequireRole roles={["admin"]}>
      <FeatureGuard feature="automations">
        <Suspense fallback={<Loader />}><AutomationRunsPage /></Suspense>
      </FeatureGuard>
    </RequireRole>} />
        <Route path={`/:company/my-orders`} element={<RequireRole roles={["client", "admin", "manager", "agent"]}>
          <Suspense fallback={<Loader />}>
            <ClientOrdersPage />
          </Suspense>
        </RequireRole>} />
          <Route path={`/:company/manager/tasks`} element={<FeatureGuard feature="tasks">
            <RequireRole roles={["manager"]}>
              <ManagerGuard permAll={["view_tasks"]}>
                <Suspense fallback={<PageLoader contentType="table" />}><ManagerTasksPage /></Suspense>
              </ManagerGuard>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/about`} element={<FeatureGuard feature="about">
            <Suspense fallback={<Loader />}><About /></Suspense>
          </FeatureGuard>} />
          <Route path={`/:company/guide`} element={<RequireRole roles={["admin", "manager", "agent", "client"]}>
            <Suspense fallback={<Loader />}><Guide /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/privacy`} element={
            <Suspense fallback={<Loader />}><PrivacyPolicyCRM /></Suspense>
          } />
          <Route path={`/:company/terms`} element={
            <Suspense fallback={<Loader />}><TermsAndConditions /></Suspense>
          } />
          <Route path={`/:company/agents`} element={<FeatureGuard feature="agents">
            <RequireRole roles={["admin", "manager", "agent"]}>
              <ManagerGuard permAny={["view_agent", "view_potential_clients"]}>
                <Suspense fallback={<PageLoader contentType="table" />}><AgentsPage /></Suspense>
              </ManagerGuard>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/issues`} element={<FeatureGuard feature="issues">
            <RequireRole roles={["admin", "manager", "agent", "client", "vendor"]}>
              <Suspense fallback={<Loader />}><IssuesPage /></Suspense>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/admin/issues`} element={<RequireRole roles={["admin", "superadmin"]}>
            <Suspense fallback={<Loader />}><AdminIssuesPage /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/admin/calendar`} element={<FeatureGuard feature="calendar">
            <RequireRole roles={["admin"]}>
              <Suspense fallback={<Loader />}><UnifiedCalendarWrapper /></Suspense>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/admin/settings`} element={<FeatureGuard feature="settings">
            <RequireRole roles={["admin"]}>
              <Suspense fallback={<PageLoader contentType="form" />}><AdminSettings /></Suspense>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/manager/settings`} element={<FeatureGuard feature="settings">
            <RequireRole roles={["manager"]}>
              <Suspense fallback={<PageLoader contentType="form" />}><ManagerSettings /></Suspense>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/agent/settings`} element={<FeatureGuard feature="settings">
            <RequireRole roles={["agent"]}>
              <Suspense fallback={<PageLoader contentType="form" />}><AgentSettings /></Suspense>
            </RequireRole>
          </FeatureGuard>} />
          <Route path={`/:company/client/settings`} element={<RequireRole roles={["client"]}>
            <Suspense fallback={<PageLoader contentType="form" />}><ClientSettings /></Suspense>
          </RequireRole>} />
          <Route path={`/:company/ApiKeyPage`} element={<RequireRole roles={["admin", "manager"]}>
            <Suspense fallback={<Loader />}><ApiKeysPage /></Suspense>
          </RequireRole>} />

          {/* Admin / power view (managers pass through to /leads/my via LeadsIndex) */}
          <Route
            path={`/:company/leads`}
            element={
              <FeatureGuard feature="leads">
                <RequireRole roles={["admin", "manager"]}>
                  <ManagerGuard permAny={['view_leads']}>
                    <Suspense fallback={<PageLoader contentType="table" />}><LeadsIndex /></Suspense>
                  </ManagerGuard>
                </RequireRole>
              </FeatureGuard>
            }
          />

          {/* Managers' own assigned leads */}
          <Route
            path={`/:company/leads/my`}
            element={
              <RequireRole roles={['manager']}>
                <ManagerGuard permAll={['view_leads']}>
                  <Suspense fallback={<PageLoader contentType="table" />}><ManagerLeadsPage /></Suspense>
                </ManagerGuard>
              </RequireRole>
            }
          />
        </Route>
      </Route>

      {/* Redirects */}
      <Route
        path="/:company"
        element={<CompanyRedirect />}
      />
      {/* CRITICAL: Redirect /undefined/* to prevent infinite loops
          This should only be hit if something goes wrong - normal flow should never reach here
          For superadmin, routes should be /superadmin/*, not /undefined/*
      */}
      <Route
        path="/undefined/home"
        element={<Navigate to="/superadmin/home" replace />}
      />
      <Route
        path="/undefined/*"
        element={<Navigate to="/superadmin/home" replace />}
      />

      {/* IMPORTANT: For any unknown path, redirect to home if authenticated, otherwise to login */}
      <Route path="*" element={<UnknownPathRedirect />} />

      <Route
        path="/privacy"
        element={
          <Suspense fallback={<Loader />}>
            <PrivacyPolicy />
          </Suspense>
        }
      />
      <Route
        path="/oauth/google/return"
        element={
          <Suspense fallback={<Loader />}>
            <OAuthGoogleReturn />
          </Suspense>
        }
      />
    </Routes>
  );
});

function LeadsPageWrapper() {
  const { company = "" } = useParams();
  return <LeadsPage company={company} />;
}

// 🔁 LiveAppShell: holds shared socket connection via SocketProvider
const LiveAppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <FeaturesProvider>
      <StorageStateProvider>
        <BootstrapProvider>
          <PostLoginLoaderWrapper />
          <SocketProvider>
            <RealtimeInvalidationWrapper />
            {children}
          </SocketProvider>
        </BootstrapProvider>
      </StorageStateProvider>
    </FeaturesProvider>
  );
};

// Wrapper component to use Bootstrap context (must be inside BootstrapProvider)
const PostLoginLoaderWrapper: React.FC = () => {
  const { isPostLoginLoading } = useBootstrap();
  
  return <PostLoginLoader visible={isPostLoginLoading} />;
};

// Component to handle realtime invalidation (needs to be inside router context)
const RealtimeInvalidationWrapper: React.FC = () => {
  const { company: urlCompany } = useParams<{ company: string }>();
  const { client } = useClient();
  
  // Get company from multiple sources (URL params, client context, localStorage)
  const company = urlCompany || client?.company || (() => {
    try {
      const clientData = JSON.parse(localStorage.getItem('client') || '{}');
      return clientData?.company;
    } catch {
      return undefined;
    }
  })();
  
  // Dynamic import to avoid circular dependencies
  const { useRealtimeInvalidation } = require('./hooks/useRealtimeInvalidation');
  useRealtimeInvalidation(company);
  return null;
};

const App: React.FC = () => {
  useEqualWebAccessibility();

  return (
    <Router>
      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        pauseOnHover
        pauseOnFocusLoss
        closeOnClick
        draggable
        theme="dark"
        hideProgressBar
        toastClassName={() =>
          'relative flex items-center gap-3 w-auto min-w-[260px] max-w-md rounded-xl bg-slate-900/95 text-slate-100 shadow-[0_15px_40px_-15px_rgba(15,23,42,0.65)] backdrop-blur-md border border-white/5 px-4 py-3'
        }
        className="!w-auto !min-w-[260px] !max-w-md toastify-custom-container"
        progressClassName={() => 'bg-gradient-to-r from-amber-400 via-rose-400 to-sky-400 h-1 rounded-full'}
        closeButton={true}
        icon={({ type }) => {
          const base = 'w-9 h-9 rounded-full flex items-center justify-center shadow-inner';
          switch (type) {
            case 'success':
              return <span className={`${base} bg-emerald-500/15 text-emerald-300 border border-emerald-500/30`}>✔</span>;
            case 'error':
              return <span className={`${base} bg-rose-500/15 text-rose-300 border border-rose-500/30`}>✖</span>;
            case 'warning':
              return <span className={`${base} bg-amber-500/15 text-amber-300 border border-amber-500/30`}>⚠</span>;
            default:
              return <span className={`${base} bg-sky-500/15 text-sky-300 border border-sky-500/30`}>ℹ</span>;
          }
        }}
        limit={3}
        newestOnTop
        data-testid="toast-container"
      />
      {/* 👇 one place to make everything live without touching pages */}
      <LiveAppShell>
        <CompanyRoutes />
        <BlockedUserModal />
        <BlockedUserSocketModal />
        <SessionExpiredModal />
        <ForceRefreshModal />
        <ManagerPermissionsChangedModal />
        <GlobalReminderPopup />
        <StickyNotes />
        <Calculator />
      </LiveAppShell>
    </Router>
  );
};

export default App;
